function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { useRef, useState } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import { spacing4, spacing8, spacing16, spacing32, info40, text120, breakWidth600, fontWeightSemiBold, spacing24 } from "@churchofjesuschrist/eden-style-constants";
import { ArrowDropDown } from "@churchofjesuschrist/eden-icons";
import { Title6 } from "@churchofjesuschrist/eden-headings";
import EdenCallout from "@churchofjesuschrist/eden-callout";
const borderWidth = "2px";
const Root = styled.div.withConfig({
  componentId: "sc-1azbsbd-0"
})(["& + &{margin-inline-start:-", ";@media (min-width:", "){margin-inline-start:-", ";}}"], spacing16, breakWidth600, spacing32);
const Spacing = styled.div.withConfig({
  componentId: "sc-1azbsbd-1"
})(["padding:0 ", ";@media (min-width:", "){padding:0 ", ";}"], spacing16, breakWidth600, spacing32);
const Link = styled.a.withConfig({
  componentId: "sc-1azbsbd-2"
})(["text-decoration:none;color:", ";", ";background:transparent;padding:calc(", " - 1px) 0 calc(", " - 1px);margin:0;white-space:nowrap;display:flex;align-items:center;&:hover{color:", ";border-block-end-color:", ";cursor:pointer;}@media (min-width:", "){padding:calc(", " + ", ") 0 calc(", " + ", " - ", ");}"], _ref => {
  let {
    current,
    active
  } = _ref;
  return current || active ? info40 : text120;
}, _ref2 => {
  let {
    current
  } = _ref2;
  return `border-block-end: ${borderWidth} solid ${current ? info40 : "transparent"}`;
}, spacing4, spacing16, info40, info40, breakWidth600, spacing4, borderWidth, spacing16, spacing4, borderWidth);
const Button = styled.button.withConfig({
  componentId: "sc-1azbsbd-3"
})(["border:0;color:", ";", ";background:transparent;padding:calc(", " - 1px) 0 calc(", " - 1px);margin:0;white-space:nowrap;display:flex;align-items:center;&:hover{color:", ";border-block-end-color:", ";cursor:pointer;}@media (min-width:", "){padding:calc(", " + ", ") 0 calc(", " + ", " - ", ");}"], _ref3 => {
  let {
    active
  } = _ref3;
  return active ? info40 : text120;
}, () => `border-block-end: ${borderWidth} solid transparent`, spacing4, spacing16, info40, info40, breakWidth600, spacing4, borderWidth, spacing16, spacing4, borderWidth);
const ButtonWrapper = styled.div.withConfig({
  componentId: "sc-1azbsbd-4"
})(["display:flex;flex-flow:column;height:100%;"]);
const ArrowWrapper = styled.div.withConfig({
  componentId: "sc-1azbsbd-5"
})(["display:flex;margin-inline-start:", ";", ""], spacing4, _ref4 => {
  let {
    active
  } = _ref4;
  return active ? "transform: rotate(180deg)" : "";
});
const Callout = styled(EdenCallout).withConfig({
  componentId: "sc-1azbsbd-6"
})(["width:100%;@media (min-width:", "){max-width:14rem;}"], breakWidth600);
const CalloutBody = styled.div.withConfig({
  componentId: "sc-1azbsbd-7"
})(["padding:", " 0 ", ";@media (min-width:", "){padding:", " 0 ", ";}"], spacing8, spacing16, breakWidth600, spacing16, spacing24);
const CalloutLink = styled.a.withConfig({
  componentId: "sc-1azbsbd-8"
})(["text-decoration:none;color:", ";padding:", " 0;display:block;cursor:pointer;&:hover{text-decoration:underline;color:", ";}"], text120, spacing8, info40);
const CalloutLinkText = styled(Title6).withConfig({
  componentId: "sc-1azbsbd-9"
})(["padding:0 ", ";font-weight:", ";", " @media (min-width:", "){padding:0 ", ";}"], spacing16, _ref5 => {
  let {
    current
  } = _ref5;
  return current ? fontWeightSemiBold : "inherit";
}, _ref6 => {
  let {
    current
  } = _ref6;
  return `border-inline-start: 4px solid ${current ? info40 : "transparent"};`;
}, breakWidth600, spacing32);

const SubNavigationItem = _ref7 => {
  let {
    text = "",
    items = [],
    current: currentProp,
    activeIndex,
    index,
    onOpen,
    onClose,
    ...props
  } = _ref7;
  // The item is "current" if the prop is present or any one of its items is current.
  const current = currentProp || !!items.find(_ref8 => {
    let {
      current
    } = _ref8;
    return current;
  });
  const buttonRef = useRef(null);
  const [ignoreMouseEnter, setIgnoreMouseEnter] = useState(false);
  return /*#__PURE__*/React.createElement(Root, {
    "data-testid": "item"
  }, /*#__PURE__*/React.createElement(Spacing, null, items.length > 0 ? /*#__PURE__*/React.createElement(ButtonWrapper, null, /*#__PURE__*/React.createElement(Button, _extends({}, props, {
    current: current,
    active: activeIndex === index,
    "data-testid": "item-button",
    ref: buttonRef,
    onTouchStart: event => {
      var _props$onTouchStart;

      setIgnoreMouseEnter(true);
      (_props$onTouchStart = props.onTouchStart) === null || _props$onTouchStart === void 0 ? void 0 : _props$onTouchStart.call(props, event);
    },
    onMouseEnter: event => {
      var _props$onMouseEnter;

      if (activeIndex !== null && !ignoreMouseEnter) {
        onOpen(index);
      }

      (_props$onMouseEnter = props.onMouseEnter) === null || _props$onMouseEnter === void 0 ? void 0 : _props$onMouseEnter.call(props, event);
    },
    onClick: event => {
      var _props$onClick;

      onOpen(index);
      setIgnoreMouseEnter(false);
      (_props$onClick = props.onClick) === null || _props$onClick === void 0 ? void 0 : _props$onClick.call(props, event);
    }
  }), /*#__PURE__*/React.createElement(Title6, null, text), /*#__PURE__*/React.createElement(ArrowWrapper, {
    active: activeIndex === index
  }, /*#__PURE__*/React.createElement(ArrowDropDown, {
    size: "0.75em"
  }))), /*#__PURE__*/React.createElement(Callout, {
    open: activeIndex === index,
    "data-testid": "item-callout",
    noArrow: true,
    forRef: buttonRef,
    onClickOutside: () => {
      onClose(index);
    }
  }, /*#__PURE__*/React.createElement(CalloutBody, null, items.map((_ref9, i) => {
    let {
      text,
      current,
      onClick,
      ...item
    } = _ref9;
    return /*#__PURE__*/React.createElement(CalloutLink, _extends({
      onClick: event => {
        onClose(index);
        onClick === null || onClick === void 0 ? void 0 : onClick(event);
      }
    }, item, {
      key: i
    }), /*#__PURE__*/React.createElement(CalloutLinkText, {
      current: current
    }, text));
  })))) : /*#__PURE__*/React.createElement(Link, _extends({
    onMouseEnter: event => {
      event.preventDefault();
      event.stopPropagation();
    }
  }, props, {
    current: current
  }), /*#__PURE__*/React.createElement(Title6, null, text))));
};

SubNavigationItem.propTypes = {
  /** The text to display */
  text: PropTypes.string.isRequired,

  /** An array of items to show in a callout. See SubNavigationCallout component for shape. */
  items: PropTypes.array,

  /** Whether the item is currently navigated to. */
  current: PropTypes.bool,

  /** The index of the active navigation item */
  activeIndex: PropTypes.number,

  /** The index of the current navigation item */
  index: PropTypes.number,

  /** Callback to execute when callout is to open */
  onOpen: PropTypes.func,

  /** Callback to execute when callout is to close */
  onClose: PropTypes.func
};
export default SubNavigationItem;